import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { SlideImage } from '@chemist2u/types-client/C2U/ParseObjects';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-slide',
    templateUrl: './slide.component.html',
    styleUrls: ['./slide.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: []
})
export class SlideComponent implements OnInit {
  private sanitizer = inject(DomSanitizer);
  public slide = input.required<SlideImage>();
  public large = input<boolean>(false)

  public bgColor = computed<string>(() => `linear-gradient(78deg, #FFF6F5 0%, ${this.slide().backgroundColor} 100%)`);
  public borderColor = computed<string>(() => this.slide().backgroundColor);
  public dashColor = computed<string>(() => this.darkenHex(this.slide().backgroundColor));

  public imageSrc = computed<string>(() => this.slide().image.url());
  public title = computed<string | undefined>(() => this.slide().title);
  public description = computed<string | undefined>(() => this.slide().description);
  public htmlContent = computed<string | undefined>(() => this.slide().htmlContent);

  public safeHTML: SafeHtml | undefined = undefined;
  
  private darkenHex(hex: string, amount = 0.2) {
    // Convert hex to RGB
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);

    // Convert RGB to HSL
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h: number = 0, s: number, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
        case g: h = ((b - r) / d + 2); break;
        case b: h = ((r - g) / d + 4); break;
      }
      h /= 6;
    }

    // Darken the lightness
    l = Math.max(0, l - amount);

    // Convert HSL back to RGB
    const hslToRgb = (h: number, s: number, l: number) => {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      let r, g, b;
      if (s === 0) {
        r = g = b = l; // grayscale
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      return [
        Math.round(r * 255),
        Math.round(g * 255),
        Math.round(b * 255)
      ];
    }

    const [rd, gd, bd] = hslToRgb(h, s, l);
    return "#" + [rd, gd, bd].map(x => x.toString(16).padStart(2, '0')).join('');
  }
  
  ngOnInit() {
    if (!this.htmlContent()) return;
    this.safeHTML = this.sanitizer.bypassSecurityTrustHtml(this.htmlContent()!);
  }
}
